import React, {useState} from "react";
import BlockButton from "../components/block-button";
import UnderlineButton from "../components/underline-button";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {
  CapturedAddress,
  CreateWatchOrderResponse,
  InquireAddressFieldsResponse,
  planState,
} from "../states/plan-state/plan-atoms";
import AsyncSelect from "react-select/async";
import {addressAutoComplete, createWatchOrder, getAddress, inquireAddressFields,} from "../services/pairing-service";
import {IoIosSearch} from "react-icons/io";
import {components, DropdownIndicatorProps} from "react-select";
import FormInput from "../components/form-input";
import FormSelect from "../components/form-select";
import {useForm} from "react-hook-form";
import {Flow} from "../models/flow-enum";

const CustomDropdownIndicator = (props: DropdownIndicatorProps<any, any>) => {
  return (
    <components.DropdownIndicator {...props}>
      <IoIosSearch />
    </components.DropdownIndicator>
  );
};

type FormData = {
  civicNumber: string;
  suffix: string;
  unit: string;
  streetName: string;
  city: string;
  postalCode: string;
};

const RegisterNewAddress = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(true);
  const [displayForm, setDisplayForm] = useState(false);
  const [autoCompleteAddress, setAutoCompleteAddress] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  const {
    register,
    watch,
    formState: { isValid },
  } = useForm<FormData>();
  const { createWatchOrderResponse, emergencyAddress, inquireCompanionFlowResponse, companionPlansResponse, serviceAddress } = useRecoilValue(planState);
  const setPlanState = useSetRecoilState(planState);
  const [selectedCountry, setSelectedCountry] = useState("CA");
  const [selectedProvince, setSelectedProvince] = useState("QUEBEC");
  const [addressFields, setAddressFields] = useState(
    {} as InquireAddressFieldsResponse,
  );
  const watchedValues = watch();

  const countries = [
    {
      value: "CA",
      label: "CANADA",
    },
  ];

  const buildSubmitRequest = () => {
    return {
      ...createWatchOrderResponse,
      capturedAddress: {
        ...createWatchOrderResponse?.capturedAddress,
        updated: localStorage.getItem("isChangedAddress") === "1",
        tmoBillAccountId: createWatchOrderResponse?.tmoBillingAccountId,
        watchAccountIds:
        createWatchOrderResponse?.capturedAddress?.watchAccountIds,
        parameters: [
          {
            shortCode: "addressType",
            value: checked ? "new" : "",
          },
        ],
      },
    };
  };

  const fetchOptions = async (inputValue: string) => {
    if (!inputValue) return [];

    try {
      const response = await addressAutoComplete({ searchString: inputValue });
      const data = await response;
      return data.map((response) => ({
        label: response.addressDescription,
        value: response.addressId,
      }));
    } catch (error) {
      console.error("Error fetching data: ", error);
      return [];
    }
  };

  const callAddressFields = () => {
    inquireAddressFields().then((res) => {
      setAddressFields(res);
      setDisplayForm(true);
    });
    setDisplayForm(true);
  };

  const formIsValid = () => {
    return isValid && !!selectedCountry && !!selectedProvince;
  };

  const transformFormToAddressObject = () => {
    const fullAddress = [
      watchedValues.civicNumber,
      watchedValues.streetName,
      watchedValues.city,
      selectedProvince,
      watchedValues.postalCode,
    ];
    const fullAddressText = fullAddress.filter(Boolean).join(" ");
    return {
      cntryCode: selectedCountry,
      cntryName: selectedCountry === "CA" ? "CANADA" : "UNITED STATES",
      addrDesc: fullAddressText,
      cityName: watchedValues.city,
      strtName: watchedValues.streetName,
      pstlCode: watchedValues.postalCode,
      stateCode: selectedProvince,
      strtNum: watchedValues.civicNumber,
      unitNo: watchedValues?.unit || null,
      suffix: watchedValues?.suffix || null,
      updated: localStorage.getItem("isChangedAddress") === "1",
      tmoBillAccountId: createWatchOrderResponse?.tmoBillingAccountId,
      watchAccountIds:
      createWatchOrderResponse?.capturedAddress?.watchAccountIds,
      parameters: [
        {
          shortCode: "addressType",
          value: "new",
        },
      ],
    } as CapturedAddress;
  };

  const goNextPage = () => {
    if (!checked) {
      if (displayForm) {
        if (formIsValid()) {
          setPlanState((prevState) => ({
            ...prevState,
            selectedAddressFromForm: transformFormToAddressObject(),
            selectedAutoCompleteAddressObject: {} as CapturedAddress,
          }));
          navigateTermsAndConditionPage();
        }
      } else {
        if (!autoCompleteAddress) {
          return;
        }
        getAddress(autoCompleteAddress).then((res: CapturedAddress) => {
          if (!!res.subAddrId) {
            setPlanState((prevState) => ({
              ...prevState,
              selectedAutoCompleteAddressObject: {
                ...res,
                tmoBillAccountId: createWatchOrderResponse?.tmoBillingAccountId,
                watchAccountIds:
                createWatchOrderResponse?.capturedAddress?.watchAccountIds,
                updated: localStorage.getItem("isChangedAddress") === "1",
                parameters: [
                  {
                    shortCode: "addressType",
                    value: "new",
                  },
                ],
              },
              selectedAddressFromForm: {} as CapturedAddress,
              createWatchOrderResponse: {
                ...prevState.createWatchOrderResponse,
                capturedAddress: {
                  updated: localStorage.getItem("isChangedAddress") === "1",
                  ...res,
                  tmoBillAccountId:
                  createWatchOrderResponse?.tmoBillingAccountId,
                  watchAccountIds:
                  createWatchOrderResponse?.capturedAddress?.watchAccountIds,
                  parameters: [
                    {
                      shortCode: "addressType",
                      value: "new",
                    },
                  ],
                },
              },
            }));
          }
        });
        setPlanState((prevState) => ({
          ...prevState,
          selectedAutoCompleteAddress: autoCompleteAddress,
        }));
        navigateTermsAndConditionPage();
      }
    } else {
      if (!autoCompleteAddress) {
        setPlanState((prevState) => ({
          ...prevState,
          createWatchOrderResponse: buildSubmitRequest(),
        }));
        navigateTermsAndConditionPage();
      }
    }
  };

  const navigateBackPage = () => {
    navigate("/activate-smartwatch");
  };

  const navigateTermsAndConditionPage = () => {
    setLoading(true);
    if(createWatchOrderResponse?.capturedAddress?.addrDesc) {
      setLoading(false);
      navigate("/terms-and-conditions");
    }
    else {
      createWatchOrder({
        iccid:
          localStorage.getItem("primaryDeviceIccid") ||
          localStorage.getItem("primaryIccid") ||
          "",
        accountId: Number(inquireCompanionFlowResponse.tmoBillingAccountId),
        planOfferId:
          inquireCompanionFlowResponse?.nextFlowPage ===
          Flow.NON_PAIRED_PLAN_FOUND
            ? null
            : Number(companionPlansResponse?.plans[0].id),
        eid: JSON.parse(localStorage.getItem("watchInfo")!).eid,
        pairAccountId: inquireCompanionFlowResponse?.devices?.length
          ? inquireCompanionFlowResponse?.devices[0].pairAccountId
          : null,
        emergencyAddress: emergencyAddress,
        serviceAddress: serviceAddress,
        byodActivation: true,
      })
        .then((response: CreateWatchOrderResponse) => {
          if (response?.customerOrderId) {
            setPlanState((prevState) => ({
              ...prevState,
              customerOrderId: response?.customerOrderId,
              createWatchOrderResponse: response,
            }));
            setLoading(false);
            navigate("/terms-and-conditions");
          } else {
            setLoading(false);
            navigate("/error");
          }
        })
        .catch((error: any) => {
          setLoading(false);
          console.error("An error occurred:", error.message);
          navigate("/error");
        });
    }
  };

  const navigateLearnMore = (): void => {
    window.location.href = i18n.language.startsWith("en")
      ? process.env.REACT_APP_LEARN_MORE_URL_EN || ""
      : process.env.REACT_APP_LEARN_MORE_URL_FR || "";
  };

  return (
    <div className="layout-default">
      <h2 className="layout-default-left watch-title">
        {t("registerAddressTitle")}
      </h2>

      <h3 className="layout-default-left">{t("registerAddressSubTitle")}</h3>
      <p>{t("registerAddressInfo")}</p>

      <p>{t("registerAddressSubInfo")}</p>

      <UnderlineButton
        className="underline-button-text-normal layout-default-left"
        text={t("learnMoreLink")}
        click={navigateLearnMore}
      />

      <div className="checkbox-container">
        <input
          data-testid="service-address-checkbox"
          type="checkbox"
          onChange={(e) => {
            setChecked(e.target.checked);
            setDisplayForm(false);
          }}
          checked={checked}
        />
        <div className="checkbox-description">
          <span>{t("serviceAddressText")}</span>
          <span data-testid="address-text">
            {emergencyAddress?.addrDesc || createWatchOrderResponse?.capturedAddress?.addrDesc}
          </span>
        </div>
      </div>

      {!checked ? (
        <div className="layout-default-left">
          {!displayForm ? (
            <AsyncSelect
              id="auto-complete"
              inputId="auto-complete-address-select"
              components={{
                DropdownIndicator: CustomDropdownIndicator,
                IndicatorSeparator: () => null,
              }}
              className="block-select"
              cacheOptions
              onChange={(e) => setAutoCompleteAddress(e?.value || "")}
              loadOptions={fetchOptions}
              defaultOptions
              placeholder={t("autoCompletePlaceholder")}
            />
          ) : (
            <></>
          )}

          {!displayForm ? (
            <UnderlineButton
              dataTestId="cant-find-address-button"
              className="underline-button-text-small layout-default-left"
              text={t("cantFindAddressText")}
              click={() => callAddressFields()}
            />
          ) : (
            <></>
          )}

          {displayForm ? (
            <form>
              <span>
                <span className="required-text">*</span> {t("requiredText")}
              </span>
              <FormInput
                data-testid="civic-number"
                {...register("civicNumber", { required: true })}
                required
                formText={t("civicNumberText")}
              />
              <FormInput
                data-testid="suffix"
                {...register("suffix")}
                formText={t("suffixText")}
              />
              <FormInput
                data-testid="unit"
                {...register("unit")}
                formText={t("unitOfficeText")}
              />
              <FormInput
                data-testid="street-name"
                {...register("streetName", { required: true })}
                formText={t("streetNameText")}
                required
              />
              <FormInput
                data-testid="city"
                {...register("city", { required: true })}
                formText={t("cityText")}
                required
              />
              <FormSelect
                data-testid="country"
                key="selected-country"
                defaultValue={selectedCountry}
                formText={t("countryText")}
                name="country"
                options={countries}
                required
                onChange={(e: any) => {
                  setSelectedCountry(e || "");
                }}
              />

              <FormSelect
                data-testid="province"
                key={selectedCountry || "CA"}
                formText={t("provinceText")}
                options={
                  addressFields.portabilityProvinceList?.map((x) => ({
                    label: x.name,
                    value: x.portabilityPrvncId,
                  })) || []
                }
                required
                value={selectedProvince}
                onChange={(e: any) => setSelectedProvince(e || "")}
              />

              <FormInput
                data-testid="postal-code"
                {...register("postalCode", { required: true })}
                formText={
                  selectedCountry === "US"
                    ? t("zipCodeText")
                    : t("postalCodeText")
                }
                required
              />
            </form>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}

      <div className="mt-3 action-buttons-container-center">
        <BlockButton
          dataTestId="continue-button"
          onClick={goNextPage}
          text={t("continueButton")}
        />
        <UnderlineButton click={navigateBackPage} text={t("cancelButton")} />
      </div>
    </div>
  );
};

export default RegisterNewAddress;
